import type { Query } from '@tanstack/react-query';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { PersistedClient, PersistQueryClientOptions, Persister } from '@tanstack/react-query-persist-client';

/** Seven days at most (spec 3A §6.4). Queries live in memory as long, so a restored copy isn't collected early. */
export const CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Bump when a kept answer changes shape, so an old copy is dropped rather than misread. */
const CACHE_BUSTER = '3a-1';

/** Home, Records, entries opened and Customers (spec 3A §6.4, 3B §8.8). Not the Fix sheets' lookups. */
const KEPT = new Set(['report.summary', 'debt.list', 'entries.toCheck', 'alerts.list', 'entries.list', 'entries.pages', 'entries.get', 'customers.list', 'customer.get']);

/** Who you are and the screens' reads, only when they succeeded. Media never enters the cache at all. */
export function keepOnDevice(query: Pick<Query, 'queryKey' | 'state'>): boolean {
  if (query.state.status !== 'success') return false;
  const [first, name] = query.queryKey;
  if (first === 'me') return query.queryKey.length === 1;
  return typeof name === 'string' && KEPT.has(name);
}

/** Lists read by cursor, kept as their first page only. */
const PAGED = new Set(['entries.pages', 'customers.list', 'customer.get']);

/** Records and Customers keep their first page only. */
export function firstPagesOnly(client: PersistedClient): PersistedClient {
  return {
    ...client,
    clientState: {
      ...client.clientState,
      queries: client.clientState.queries.map((q) => {
        const data = q.state.data as { pages: unknown[]; pageParams: unknown[] } | undefined;
        if (!PAGED.has(q.queryKey[1] as string) || !Array.isArray(data?.pages)) return q;
        return { ...q, state: { ...q.state, data: { pages: data!.pages.slice(0, 1), pageParams: data!.pageParams.slice(0, 1) } } };
      }),
    },
  };
}

export interface CacheStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

export function createDevicePersister(storage: CacheStorage): Persister {
  return createAsyncStoragePersister({
    storage,
    key: 'zibanu:cache',
    throttleTime: 1000,
    serialize: (client) => JSON.stringify(firstPagesOnly(client)),
  });
}

export function devicePersistOptions(persister: Persister): Omit<PersistQueryClientOptions, 'queryClient'> {
  return { persister, maxAge: CACHE_MAX_AGE_MS, buster: CACHE_BUSTER, dehydrateOptions: { shouldDehydrateQuery: keepOnDevice } };
}
